<template>
  <div class="qa-page">
    <!-- Page header -->
    <div class="qa-header">
      <div>
        <h1 class="qa-title">منتدى الوساطة</h1>
        <p class="qa-subtitle">شارك أسئلتك، ابحث في المنشورات، وتابع التعليقات والإعجابات</p>
      </div>
      <div class="qa-header-actions">
        <button type="button" class="qa-add-question-btn" @click="showAddQuestionDialog = true" title="إضافة سؤال">
          <v-icon size="24">mdi-plus</v-icon>
        </button>
        <v-chip v-if="waitingCount !== null" class="qa-waiting-chip" small>
          {{ waitingCount }} سؤال بانتظار رد
        </v-chip>
      </div>
    </div>

    <!-- =================================== Search Component ===================================-->
    <question-and-answer-search-component :filters.sync="filters" :all-users="allUsers" :active-tab="activeTab"
      @apply-filters="applyFilters" @reset-filters="resetFilters" @switch-tab="switchTab" />

    <!-- =================================== Add Question Dialog ===================================-->
    <div class="text-center">
      <v-dialog v-model="showAddQuestionDialog" width="500" class="qa-compose-dialog">
        <v-card>
          <v-card-title class="add-new-req">
            اضافة سؤال جديد
          </v-card-title>

          <v-card-text>
            <div class="mb-1" style="font-weight: 600">السؤال</div>
            <v-row>
              <v-col cols="12" class="mt-2">
                <v-textarea v-model="newPost" placeholder="اكتب سؤالك هنا..." rows="4" auto-grow hide-details outlined
                  class="qa-field" />
              </v-col>
              <v-col cols="12" class="py-0">
                <v-switch v-model="post_privacy" inset hide-details label="نشر كمجهول" class="mt-4 qa-switch"
                  color="#5c325e" />
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="qa-btn qa-btn-outline" color="button" text @click="closeAddQuestionDialog">
              إغلاق
            </v-btn>

            <v-btn class="qa-btn qa-btn-primary qa-btn-lg" color="button" :disabled="submitting" depressed
              @click="submitPost">
              <v-icon size="20" class="qa-icon-in-btn">mdi-send</v-icon>
              {{ submitting ? 'جاري النشر...' : 'نشر السؤال' }}
            </v-btn>

          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <!-- =================================== loading & empty & posts=================================== -->
    <!-- Loading -->
    <div v-if="loading" class="qa-loading">
      <v-progress-circular indeterminate size="48" width="4" color="#5c325e" />
      <span>جاري التحميل...</span>
    </div>
    <!-- =================================== =================================== -->

    <!-- =================================== empty=================================== -->
    <!-- Empty -->
    <v-card v-else-if="posts.length === 0" class="qa-card qa-empty" elevation="0">
      <v-icon size="56" class="qa-icon-muted">mdi-forum-outline</v-icon>
      <p>لا توجد منشورات مطابقة للبحث</p>
      <button type="button" class="qa-btn qa-btn-outline" @click="resetFilters">عرض الكل</button>
    </v-card>
    <!-- =================================== =================================== -->

    <!-- =================================== posts=================================== -->
    <!-- Posts -->
    <div v-else class="qa-posts">
      <v-card v-for="post in posts" :key="post.id" class="qa-card qa-post" elevation="0">
        <div class="qa-post-header">
          <div class="qa-post-author">
            <v-avatar color="#5c325e" size="48" class="qa-avatar-sm">
              <span class="qa-avatar-text">{{ getInitials(post.user?.name) }}</span>
            </v-avatar>
            <div>
              <p class="qa-post-name">{{ post.user?.name || 'مستخدم' }}</p>
              <p class="qa-post-date">{{ post.created_at }}</p>
            </div>
          </div>
          <div class="qa-post-meta">
            <button type="button" class="qa-like-chip" @click="listQuestionLikes(post.id)">
              <v-icon size="16" class="qa-icon-in-btn">mdi-thumb-up-outline</v-icon>
              {{ post.likes_count }}
            </button>
            <button type="button" class="qa-icon-btn qa-icon-btn--danger" title="حذف" @click="deletePost(post.id)">
              <v-icon size="20">mdi-trash-can-outline</v-icon>
            </button>
          </div>
        </div>

        <p class="qa-post-question">{{ post.question }}</p>

        <div class="qa-post-actions">
          <button type="button" class="qa-text-btn" @click="toggleComments(post.id)">
            <v-icon size="20" class="qa-icon-accent">mdi-comment-text-outline</v-icon>
            التعليقات ({{ post.comments?.length || 0 }})
          </button>
          <v-spacer />
          <button type="button" class="qa-icon-btn" @click="likePost(post.id)">
            <v-icon size="22" :class="post.is_liked_by_user ? 'qa-icon-accent' : 'qa-icon-muted'">
              {{ post.is_liked_by_user ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}
            </v-icon>
          </button>
        </div>

        <v-expand-transition>
          <div v-if="expandedPostId === post.id" class="qa-comments">
            <div v-for="comment in post.comments" :key="comment.id" class="qa-comment">
              <div class="qa-comment-top">
                <span class="qa-comment-author">{{ comment.user?.name }}</span>
                <span class="qa-comment-date">{{ comment.created_at }}</span>
              </div>
              <p class="qa-comment-text">{{ comment.answer }}</p>
              <div class="qa-comment-actions">
                <button type="button" class="qa-like-chip qa-like-chip--sm" @click="listCommentLikes(comment.id)">
                  <v-icon size="14" class="qa-icon-in-btn">mdi-thumb-up-outline</v-icon>
                  {{ comment.likes_count }}
                </button>
                <button type="button" class="qa-icon-btn" @click="likeComment(comment.id)">
                  <v-icon size="18" :class="comment.is_liked_by_user ? 'qa-icon-accent' : 'qa-icon-muted'">
                    {{ comment.is_liked_by_user ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}
                  </v-icon>
                </button>
              </div>
            </div>

            <div class="qa-add-comment">
              <v-textarea v-model="newComment" placeholder="أضف تعليقاً..." rows="2" auto-grow hide-details outlined
                class="qa-field" />
              <button type="button" class="qa-btn qa-btn-primary mt-3" @click="addComment(post.id)">
                <v-icon size="18" class="qa-icon-in-btn">mdi-send</v-icon>
                إرسال التعليق
              </button>
            </div>
          </div>
        </v-expand-transition>
      </v-card>
    </div>
    <!-- =================================== =================================== -->

    <!-- =================================== pagination=================================== -->
    <!-- Pagination -->
    <div v-if="pages > 1" class="qa-pagination">
      <button type="button" class="qa-icon-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
        <v-icon size="28" class="qa-icon-accent">mdi-chevron-right</v-icon>
      </button>
      <span class="qa-pagination-text">صفحة {{ currentPage }} من {{ pages }} — {{ results }} منشور</span>
      <button type="button" class="qa-icon-btn" :disabled="currentPage === pages" @click="changePage(currentPage + 1)">
        <v-icon size="28" class="qa-icon-accent">mdi-chevron-left</v-icon>
      </button>
    </div>
    <!-- =================================== =================================== -->

    <!-- =================================== likes dialog=================================== -->
    <!-- Likes dialog -->
    <v-dialog v-model="dialogOfQuestionLikeusers" max-width="500" class="qa-compose-dialog">
      <v-card>
        <v-card-title class="add-new-req">
          <v-icon size="24" class="qa-icon-accent ml-2">mdi-thumb-up</v-icon>
          {{ likeText }}
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 360px; overflow-y: auto">
          <v-row dense>
            <v-col v-for="user in likedUsers" :key="user.id" cols="12" sm="6" class="d-flex align-center">
              <v-avatar color="#5c325e" size="44" class="ml-3">
                <span class="qa-avatar-text">{{ getInitials(user.name) }}</span>
              </v-avatar>
              <div>
                <p class="mb-0 font-weight-medium">{{ user.name }}</p>
                <small class="qa-muted">{{ user.job_title || '—' }}</small>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <button type="button" class="qa-btn qa-btn-outline" @click="dialogOfQuestionLikeusers = false">إغلاق</button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- =================================== =================================== -->

  </div>
</template>

<script>
import { axiosInstance } from "@/axios/config";
import QuestionAndAnswerSearchComponent from "@/components/QuestionAndAnswerSearchComponent.vue";

const defaultFilters = () => ({
  search: "",
  author_user_id: null,
  comment_user_id: null,
  has_comments: "all",
  has_likes: "all",
  has_comment_likes: "all",
});

export default {
  components: {
    QuestionAndAnswerSearchComponent,
  },
  data() {
    return {
      showAddQuestionDialog: false,
      activeTab: "all",
      posts: [],
      newPost: "",
      post_privacy: false,
      expandedPostId: null,
      newComment: "",
      loading: false,
      submitting: false,
      waitingCount: null,
      allUsers: [],
      filters: defaultFilters(),
      triStateOptions: [
        { label: "الكل", value: "all" },
        { label: "نعم", value: "yes" },
        { label: "لا", value: "no" },
      ],
      pagination: { limit: 6, count: 0 },
      currentPage: 1,
      dialogOfQuestionLikeusers: false,
      likedUsers: [],
      likeText: "",
    };
  },

  computed: {
    userName() {
      return localStorage.getItem("loggedIn_user_name") || "";
    },
    firstTwoCharFromUser() {
      return this.getInitials(this.userName);
    },
    results() {
      return this.pagination.count;
    },
    pages() {
      return Math.max(1, Math.ceil(this.pagination.count / this.pagination.limit));
    },
  },

  created() {
    this.loadUsers();
    this.fetchPosts("all");
  },

  methods: {
    getInitials(fullName) {
      if (!fullName) return "?";
      return fullName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join("");
    },

    buildSearchPayload() {
      const payload = {};
      if (this.filters.search?.trim()) payload.search = this.filters.search.trim();
      if (this.filters.author_user_id) payload.author_user_id = this.filters.author_user_id;
      if (this.filters.comment_user_id) payload.comment_user_id = this.filters.comment_user_id;
      ["has_comments", "has_likes", "has_comment_likes"].forEach((key) => {
        if (this.filters[key] && this.filters[key] !== "all") {
          payload[key] = this.filters[key];
        }
      });
      return payload;
    },

    async loadUsers() {
      try {
        const res = await axiosInstance.get("/agent/list-listAllUserForAnswer");
        const block = res.data.payload;
        this.allUsers = Array.isArray(block) ? block : (block?.data || []);
      } catch (e) {
        console.error(e);
      }
    },

    async fetchPosts(type, page = 1) {
      this.activeTab = type;
      this.currentPage = page;
      this.loading = true;

      const endpoint =
        type === "all"
          ? `/agent/list-questionaires?per_page=${this.pagination.limit}&page=${page}`
          : `/agent/list-AgentQuestionaire?per_page=${this.pagination.limit}&page=${page}`;

      try {
        const res = await axiosInstance.post(endpoint, this.buildSearchPayload());
        const block = res.data.payload.data;
        this.posts = Array.isArray(block) ? block : (block?.data || []);
        this.pagination.count = res.data.payload.count || 0;
        this.waitingCount = res.data.payload.count_of_question_waiting_response ?? null;
      } catch (error) {
        this.showPopup("error", error.response?.data?.message || "خطأ أثناء جلب المنشورات.");
      } finally {
        this.loading = false;
      }
    },

    switchTab(type) {
      this.currentPage = 1;
      this.fetchPosts(type, 1);
    },

    applyFilters() {
      this.currentPage = 1;
      this.fetchPosts(this.activeTab, 1);
    },

    resetFilters() {
      this.filters = defaultFilters();
      this.applyFilters();
    },

    changePage(newPage) {
      if (newPage < 1 || newPage > this.pages) return;
      this.fetchPosts(this.activeTab, newPage);
    },

    async submitPost() {
      if (!this.newPost.trim()) {
        this.showPopup("error", "يرجى كتابة السؤال أولاً");
        return;
      }
      this.submitting = true;
      try {
        const res = await axiosInstance.post("/agent/create-questionaires", {
          question: this.newPost,
        });
        this.showPopup("success", res.data.message);
        this.newPost = "";
        this.post_privacy = false;
        this.showAddQuestionDialog = false;
        this.fetchPosts(this.activeTab, this.currentPage);
      } catch (res) {
        this.showPopup("error", res.response?.data?.message || "تعذر النشر");
      } finally {
        this.submitting = false;
      }
    },

    toggleComments(postId) {
      this.expandedPostId = this.expandedPostId === postId ? null : postId;
    },

    async deletePost(postId) {
      try {
        const res = await axiosInstance.get(`/employee/admin/delete-questionaire/${postId}`);
        this.showPopup("success", res.data.message);
        this.fetchPosts(this.activeTab, this.currentPage);
      } catch (res) {
        this.showPopup("error", res.response?.data?.message || "تعذر الحذف");
      }
    },

    async likePost(postId) {
      try {
        const res = await axiosInstance.get(
          `/employee/funding-manager/like-questionaire/${postId}`
        );
        this.showPopup("success", res.data.message);
        this.fetchPosts(this.activeTab, this.currentPage);
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },

    async likeComment(commentId) {
      try {
        const res = await axiosInstance.get(
          `/employee/funding-manager/like-comment/${commentId}`
        );
        this.showPopup("success", res.data.message);
        this.fetchPosts(this.activeTab, this.currentPage);
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },

    async addComment(postId) {
      if (!this.newComment.trim()) {
        this.showPopup("error", "يرجى كتابة تعليق قبل الإرسال.");
        return;
      }
      try {
        const res = await axiosInstance.post(
          `/employee/funding-manager/answer-questionaires/${postId}`,
          { answer: this.newComment }
        );
        this.showPopup("success", res.data.message);
        this.newComment = "";
        this.fetchPosts(this.activeTab, this.currentPage);
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },
    closeAddQuestionDialog() {
      this.showAddQuestionDialog = false;
      this.newPost = "";
      this.post_privacy = false;
    },

    async listQuestionLikes(questionId) {
      this.likeText = "المعجبين بالسؤال";
      try {
        const res = await axiosInstance.get(
          `/employee/funding-manager/list-question-likes/${questionId}`
        );
        this.likedUsers = res.data.payload.data || [];
        this.dialogOfQuestionLikeusers = true;
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },

    async listCommentLikes(commentId) {
      this.likeText = "المعجبين بالتعليق";
      try {
        const res = await axiosInstance.get(
          `/employee/funding-manager/list-comment-likes/${commentId}`
        );
        this.likedUsers = res.data.payload.data || [];
        this.dialogOfQuestionLikeusers = true;
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },
  },
};
</script>

<style scoped>
.qa-page {
  --qa-bg: #f7f8fb;
  --qa-card: #ffffff;
  --qa-border: #d1d5db;
  --qa-text: #0f172a;
  --qa-muted: #475569;
  --qa-accent: #5c325e;
  --qa-accent-hover: #4a284c;
  --qa-accent-soft: rgba(92, 50, 94, 0.12);
  --qa-danger: #dc2626;
  --qa-danger-soft: rgba(220, 38, 38, 0.12);
  --qa-btn-text: #ffffff;
  --qa-outline-text: #5c325e;
  --qa-icon: #5c325e;
  --qa-icon-muted: #64748b;
  --qa-segment-bg: #f8fafc;
  background: var(--qa-bg);
  color: var(--qa-text);
  padding: 20px 16px 32px;
  max-width: 1080px;
  margin: 0 auto;
  font-size: 15px;
}

.dark .qa-page {
  --qa-bg: #0f172a;
  --qa-card: #1f2937;
  --qa-border: #334155;
  --qa-text: #e2e8f0;
  --qa-muted: #94a3b8;
  --qa-accent: #c084fc;
  --qa-accent-hover: #a855f7;
  --qa-accent-soft: rgba(192, 132, 252, 0.18);
  --qa-danger: #f87171;
  --qa-danger-soft: rgba(248, 113, 113, 0.18);
  --qa-btn-text: #ffffff;
  --qa-outline-text: #e9d5ff;
  --qa-icon: #c084fc;
  --qa-icon-muted: #94a3b8;
  --qa-segment-bg: #111827;
  background: var(--qa-bg);
  color: var(--qa-text);
}

.qa-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--qa-border);
}

.qa-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qa-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--qa-text);
  margin: 0 0 6px;
}

.qa-subtitle {
  color: var(--qa-muted);
  margin: 0;
  font-size: 1rem;
}

.qa-add-question-btn {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--qa-border);
  background: var(--qa-card);
  color: var(--qa-accent);
  cursor: pointer;
  transition: background 0.2s, transform 0.2s, border-color 0.2s;
}

.qa-add-question-btn:hover {
  background: var(--qa-accent-soft);
  transform: translateY(-1px);
  border-color: var(--qa-accent);
}

.qa-waiting-chip {
  background: var(--qa-accent-soft) !important;
  color: var(--qa-accent) !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  padding: 8px 14px !important;
  height: auto !important;
}

.qa-card {
  background: var(--qa-card) !important;
  border: 1px solid var(--qa-border);
  border-radius: 14px !important;
  margin-bottom: 18px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.dark .qa-card {
  box-shadow: none;
}

.qa-dialog-card {
  border-radius: 16px !important;
  background: var(--qa-card) !important;
}

.qa-dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  color: var(--qa-text) !important;
}

.qa-page>>>.v-dialog>.v-card {
  background: #ffffff !important;
  color: #0f172a !important;
  border: 1px solid #d1d5db !important;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.16) !important;
}

.qa-page>>>.v-dialog .v-card__title {
  background: #ffffff !important;
  color: #0f172a !important;
  padding: 16px 20px 0 !important;
}

.qa-page>>>.v-dialog .v-divider {
  background: #d1d5db !important;
  border-color: #d1d5db !important;
}

.qa-page>>>.v-dialog .v-card__text {
  background: #ffffff !important;
  color: #0f172a !important;
  padding: 16px 20px !important;
}

.qa-page>>>.v-dialog .v-card__actions {
  background: #ffffff !important;
  color: #0f172a !important;
  padding: 16px 20px 20px !important;
}

.qa-page>>>.v-dialog .v-textarea>>>textarea {
  background: #f7f8fb !important;
  color: #0f172a !important;
  caret-color: #0f172a !important;
}

.qa-page>>>.v-dialog .v-textarea>>>fieldset {
  border-color: #d1d5db !important;
}

.qa-page>>>.v-dialog .v-switch>>>.v-label {
  color: #0f172a !important;
}

.qa-page>>>.v-dialog .v-input>>>.v-label {
  color: #475569 !important;
}


.dark .qa-page>>>.v-dialog>.v-card,
.dark .qa-page>>>.v-dialog .v-card__title,
.dark .qa-page>>>.v-dialog .v-card__text,
.dark .qa-page>>>.v-dialog .v-card__actions {
  background: #1f2937 !important;
  color: #e2e8f0 !important;
  border-color: #334155 !important;
}

.dark .qa-page>>>.v-dialog .v-divider {
  background: #334155 !important;
  border-color: #334155 !important;
}

.dark .qa-page>>>.v-dialog .v-textarea>>>textarea {
  background: #111827 !important;
  color: #e2e8f0 !important;
  caret-color: #e2e8f0 !important;
}

.dark .qa-page>>>.v-dialog .v-textarea>>>fieldset {
  border-color: #334155 !important;
}

.dark .qa-page>>>.v-dialog .v-switch>>>.v-label {
  color: #e2e8f0 !important;
}

.dark .qa-page>>>.v-dialog .v-input>>>.v-label {
  color: #94a3b8 !important;
}

.qa-page>>>.v-overlay__scrim {
  background: rgba(15, 23, 42, 0.45) !important;
}

.dark .qa-page>>>.v-overlay__scrim {
  background: rgba(0, 0, 0, 0.6) !important;
}


.qa-compose-actions {
  padding: 8px 20px 20px !important;
}

.qa-avatar-text {
  color: #fff !important;
  font-weight: 700;
  font-size: 1rem;
}

.qa-icon-accent {
  color: var(--qa-icon) !important;
}

.qa-icon-muted {
  color: var(--qa-icon-muted) !important;
}

.qa-icon-in-btn {
  color: inherit !important;
}

.qa-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.2s, color 0.2s, border-color 0.2s, opacity 0.2s;
  white-space: nowrap;
}

.qa-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.qa-btn-primary {
  background: var(--qa-accent);
  color: var(--qa-btn-text) !important;
}

.qa-btn-primary:hover:not(:disabled) {
  background: var(--qa-accent-hover);
}

.qa-btn-outline {
  background: var(--qa-card);
  color: var(--qa-outline-text) !important;
  border: 1.5px solid var(--qa-accent);
}

.qa-btn-outline:hover:not(:disabled) {
  background: var(--qa-accent-soft);
}

.qa-btn-lg {
  padding: 12px 24px;
  font-size: 1rem;
}

.qa-text-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: var(--qa-accent) !important;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
}

.qa-text-btn:hover {
  background: var(--qa-accent-soft);
}

.qa-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  color: var(--qa-icon);
}

.qa-icon-btn:hover:not(:disabled) {
  background: var(--qa-accent-soft);
}

.qa-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.qa-icon-btn--danger {
  color: var(--qa-danger) !important;
}

.qa-icon-btn--danger:hover {
  background: var(--qa-danger-soft);
}

.qa-like-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid var(--qa-border);
  background: var(--qa-card);
  color: var(--qa-text);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
}

.qa-like-chip:hover {
  border-color: var(--qa-accent);
  background: var(--qa-accent-soft);
}

.qa-like-chip--sm {
  padding: 4px 10px;
  font-size: 0.82rem;
}

.qa-filters-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  color: var(--qa-text) !important;
  padding: 16px 20px !important;
}

.qa-filters-body {
  padding: 20px 20px 8px !important;
}

.qa-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: var(--qa-segment-bg);
  border-radius: 0 0 14px 14px;
}

.qa-toolbar-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.qa-toolbar-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--qa-muted);
  white-space: nowrap;
}

.qa-toolbar-actions {
  gap: 10px;
}

.qa-segmented {
  display: inline-flex;
  padding: 4px;
  background: var(--qa-card);
  border: 1px solid var(--qa-border);
  border-radius: 12px;
  gap: 4px;
}

.qa-segmented-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--qa-muted);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.qa-segmented-btn:hover:not(.qa-segmented-btn--active) {
  background: var(--qa-accent-soft);
  color: var(--qa-accent);
}

.qa-segmented-btn--active {
  background: var(--qa-accent);
  color: #fff !important;
  box-shadow: 0 2px 8px rgba(92, 50, 94, 0.25);
}

.qa-segmented-btn--active .qa-icon-in-btn {
  color: #fff !important;
}

.qa-loading,
.qa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 56px 24px;
  color: var(--qa-muted);
  font-size: 1.05rem;
}

.qa-post-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 20px 0;
  gap: 16px;
}

.qa-post-author {
  display: flex;
  gap: 14px;
  align-items: center;
}

.qa-post-name {
  margin: 0;
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--qa-text);
}

.qa-post-date,
.qa-comment-date,
.qa-muted {
  color: var(--qa-muted);
  font-size: 0.85rem;
}

.qa-post-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qa-post-question {
  padding: 16px 20px;
  margin: 0;
  font-size: 1.08rem;
  line-height: 1.75;
  color: var(--qa-text);
}

.qa-post-actions {
  display: flex;
  align-items: center;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--qa-border);
  margin-top: 4px;
}

.qa-comments {
  border-top: 1px solid var(--qa-border);
  background: var(--qa-accent-soft);
  padding: 16px 20px 20px;
}

.qa-comment {
  background: var(--qa-card);
  border: 1px solid var(--qa-border);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
}

.qa-comment-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 12px;
}

.qa-comment-author {
  font-weight: 700;
  font-size: 0.92rem;
  color: var(--qa-text);
}

.qa-comment-text {
  margin: 0 0 10px;
  color: var(--qa-text);
  font-size: 0.98rem;
  line-height: 1.65;
}

.qa-comment-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qa-add-comment {
  margin-top: 12px;
}

.qa-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px 0 36px;
}

.qa-pagination-text {
  color: var(--qa-muted);
  font-size: 1rem;
  font-weight: 600;
}

.qa-page>>>.v-input__slot {
  background: var(--qa-card) !important;
  min-height: 48px !important;
}

.qa-page>>>fieldset {
  border-color: var(--qa-border) !important;
  border-width: 1.5px !important;
}

.qa-page>>>.v-label,
.qa-page>>>input,
.qa-page>>>textarea {
  color: var(--qa-text) !important;
  font-size: 0.95rem !important;
}

.qa-page>>>.v-input__prepend-inner .v-icon {
  color: var(--qa-icon) !important;
}

.qa-page>>>.v-select__selections,
.qa-page>>>.v-select__slot label {
  color: var(--qa-text) !important;
}

.qa-switch>>>.v-label {
  color: var(--qa-text) !important;
  font-weight: 600;
}

.dark .qa-page>>>.v-input__slot {
  background: #0f172a !important;
}

@media (max-width: 768px) {
  .qa-page {
    padding: 16px 12px 32px;
  }

  .qa-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .qa-toolbar-group,
  .qa-toolbar-actions {
    width: 100%;
    justify-content: center;
  }

  .qa-segmented {
    width: 100%;
  }

  .qa-segmented-btn {
    flex: 1;
    justify-content: center;
    padding: 10px 8px;
    font-size: 0.85rem;
  }

  .qa-btn {
    flex: 1;
  }
}
</style>